// ---------------------------------------------------------------------------
// Thin fetch wrapper for dev-console API calls.
// Auth will be handled by server-side routes — no client-side token management.
// ---------------------------------------------------------------------------
import { ApiErrorCode, type ApiErrorBody } from '@dev-console/api/errors';

import { DEV_CONSOLE_API_BASE_URL } from '@/config/devConsole';

export const API_BASE_URL = DEV_CONSOLE_API_BASE_URL;

export { ApiErrorCode };

export class ApiError extends Error {
  private static readonly KNOWN_CODES: ReadonlySet<string> = new Set(Object.values(ApiErrorCode));

  constructor(public status: number, public body: ApiErrorBody) {
    super('message' in body && typeof body.message === 'string' ? body.message : body.code);
    this.name = 'ApiError';
  }

  get code(): ApiErrorCode {
    return this.body.code;
  }

  is<C extends ApiErrorCode>(code: C): this is ApiError & { body: Extract<ApiErrorBody, { code: C }> } {
    return this.body.code === code;
  }

  static toErrorCode(raw: unknown): ApiErrorCode | null {
    return typeof raw === 'string' && ApiError.KNOWN_CODES.has(raw) ? (raw as ApiErrorCode) : null;
  }
}

export async function api<T = unknown>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw createApiError(res.status, body, res.statusText);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

export interface BlobDownload {
  blob: Blob;
  fileName: string | null;
}

export async function apiBlob(path: string, options: RequestInit = {}): Promise<BlobDownload> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      ...(options.body != null ? { 'Content-Type': 'application/json' } : {}),
      ...options.headers,
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw createApiError(res.status, body, res.statusText);
  }

  return {
    blob: await res.blob(),
    fileName: parseContentDispositionFileName(res.headers.get('Content-Disposition')),
  };
}

function parseContentDispositionFileName(contentDisposition: string | null): string | null {
  if (!contentDisposition) return null;

  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1].trim().replace(/^"|"$/g, ''));
  }

  const match = /filename="?([^";]+)"?/i.exec(contentDisposition);
  return match?.[1]?.trim() ?? null;
}

function createApiError(status: number, body: Record<string, unknown>, statusText: string): ApiError {
  const code = ApiError.toErrorCode(body.code);
  if (code !== null) {
    return new ApiError(status, body as unknown as ApiErrorBody);
  }
  return new ApiError(status, {
    code: ApiErrorCode.INTERNAL_SERVER_ERROR,
    message: `Malformed error response (status ${status}${statusText ? `: ${statusText}` : ''})`,
  });
}
